// 本地 AI 语音存储:IndexedDB 存每条 AI 回复的 mp3 Blob。
// key = 真实会话 id(字符串)。会话期收到服务端 done 回传的真实 id 后,前端就把消息假 id 换成真 id,
// 所以会话期与重载后历史(_id*2+1)同构、对得上;会话 id 全局唯一 → 无 hash 冲突、跨用户也不串。
// 另维护内存中"已有音频的 key 集合"做外部 store,气泡用 useSyncExternalStore 订阅,决定是否显示播放按钮。
// 淘汰:每条带写入时间戳 ts(建索引);超过 MAX_CLIPS 时按 ts 升序删最旧,封顶本地占用。

const DB_NAME = 'sb_audio'
const STORE = 'clips'
const VERSION = 2
const MAX_CLIPS = 200 // 单条 AI 回复 ~50–300KB,200 条约 10–60MB,移动端可接受

interface Clip {
    blob: Blob
    ts: number
}

let dbPromise: Promise<IDBDatabase> | null = null
function db(): Promise<IDBDatabase> {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const req = indexedDB.open(DB_NAME, VERSION)
            req.onupgradeneeded = () => {
                const d = req.result
                // v1(裸 Blob、无索引)结构不兼容 → 清掉重建(仅本地缓存,丢了下次合成会重存)
                if (d.objectStoreNames.contains(STORE)) d.deleteObjectStore(STORE)
                d.createObjectStore(STORE).createIndex('ts', 'ts')
            }
            req.onsuccess = () => resolve(req.result)
            req.onerror = () => reject(req.error)
        })
    }
    return dbPromise
}

function reqToPromise<T>(r: IDBRequest<T>): Promise<T> {
    return new Promise((res, rej) => {
        r.onsuccess = () => res(r.result)
        r.onerror = () => rej(r.error)
    })
}

let keys = new Set<string>()
const listeners = new Set<() => void>()
function setKeys(next: Set<string>) {
    keys = next // 换新实例,useSyncExternalStore 才认为变了
    for (const l of listeners) l()
}

// 超上限时按 ts 升序(最旧在前)删到 MAX_CLIPS,并把删掉的 key 从内存集合移除
async function evict(): Promise<void> {
    const store = (await db()).transaction(STORE, 'readwrite').objectStore(STORE)
    const count = await reqToPromise(store.count())
    if (count <= MAX_CLIPS) return
    let toDelete = count - MAX_CLIPS
    const removed: string[] = []
    await new Promise<void>((res, rej) => {
        const cur = store.index('ts').openCursor()
        cur.onsuccess = () => {
            const c = cur.result
            if (c && toDelete > 0) {
                removed.push(c.primaryKey as string)
                c.delete()
                toDelete--
                c.continue()
            } else {
                res()
            }
        }
        cur.onerror = () => rej(cur.error)
    })
    if (removed.length) {
        const next = new Set(keys)
        for (const k of removed) next.delete(k)
        setKeys(next)
    }
}

export const audioStore = {
    // 进 Chat 时调用:把已存的所有 key 载入内存集合(重载/重登后历史气泡据此显示播放按钮)
    async init(): Promise<void> {
        try {
            const store = (await db()).transaction(STORE, 'readonly').objectStore(STORE)
            const all = (await reqToPromise(store.getAllKeys())) as string[]
            setKeys(new Set(all))
        } catch {
            /* IDB 不可用就当无音频 */
        }
    },
    async save(key: string, blob: Blob): Promise<void> {
        try {
            const store = (await db()).transaction(STORE, 'readwrite').objectStore(STORE)
            await reqToPromise(store.put({ blob, ts: Date.now() } satisfies Clip, key))
            if (!keys.has(key)) setKeys(new Set(keys).add(key))
            await evict()
        } catch {
            /* ignore */
        }
    },
    async getBlob(key: string): Promise<Blob | null> {
        try {
            const store = (await db()).transaction(STORE, 'readonly').objectStore(STORE)
            const clip = (await reqToPromise(store.get(key))) as Clip | undefined
            return clip?.blob ?? null
        } catch {
            return null
        }
    },
    subscribe(l: () => void): () => void {
        listeners.add(l)
        return () => listeners.delete(l)
    },
    getKeys(): Set<string> {
        return keys
    }
}
